import { useCallback, useEffect, useState } from 'react';

// Shared position of the draggable floating action button (FloatingAddButton) and the
// running-timer panel that sits beside it (Timer). The position is stored as the
// distance of the main button from the viewport's right and bottom edges, so the
// default (32, 32) keeps it in the lower-right corner and a window resize keeps it
// anchored to that corner. Persisted per browser in localStorage and broadcast with a
// window event so both components stay in sync without a context provider.

const STORAGE_KEY = 'gva.fab.position';
const CHANGE_EVENT = 'fab-position-changed';
export const FAB_SIZE = 64;
export const PHONE_FAB_SIZE = 56; // main button below the `sm` breakpoint (see useIsPhone)
export const FAB_MARGIN = 8; // minimum gap kept between the button and the viewport edge
export const DEFAULT_FAB_POSITION = { right: 32, bottom: 32 };
const PHONE_DEFAULT_FAB_POSITION = { right: 16, bottom: 16 };
const PHONE_MAX_WIDTH = 600; // MUI `sm` breakpoint, same switch as useIsPhone

// Phones start nearer the corner; an explicitly dragged position is kept as stored.
const defaultFabPosition = () =>
  window.innerWidth < PHONE_MAX_WIDTH ? PHONE_DEFAULT_FAB_POSITION : DEFAULT_FAB_POSITION;

export const clampFabPosition = ({ right, bottom }) => {
  const maxRight = Math.max(FAB_MARGIN, window.innerWidth - FAB_SIZE - FAB_MARGIN);
  const maxBottom = Math.max(FAB_MARGIN, window.innerHeight - FAB_SIZE - FAB_MARGIN);
  return {
    right: Math.min(Math.max(right, FAB_MARGIN), maxRight),
    bottom: Math.min(Math.max(bottom, FAB_MARGIN), maxBottom),
  };
};

const readStored = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (parsed && Number.isFinite(parsed.right) && Number.isFinite(parsed.bottom)) {
      return clampFabPosition(parsed);
    }
  } catch (e) {
    // private mode / corrupted value: fall back to the default corner
  }
  return defaultFabPosition();
};

export const useFloatingButtonPosition = () => {
  const [position, setPositionState] = useState(readStored);
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  useEffect(() => {
    const onChange = (e) => setPositionState(e.detail);
    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
      setPositionState((p) => clampFabPosition(p));
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // persist=false while dragging (cheap live updates), true on drop.
  const setPosition = useCallback((next, { persist = true } = {}) => {
    const clamped = next ? clampFabPosition(next) : defaultFabPosition();
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: clamped }));
    if (persist) {
      try {
        if (next) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(clamped));
        else window.localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // storage unavailable: position just won't survive a reload
      }
    }
  }, []);

  // Which half of the screen the button is in — the menu opens away from the nearest
  // vertical edge and the timer panel sits on the side with more room.
  const onLeftHalf = position.right + FAB_SIZE / 2 > viewport.w / 2;
  const onTopHalf = position.bottom + FAB_SIZE / 2 > viewport.h / 2;

  return { position, setPosition, onLeftHalf, onTopHalf, viewport };
};
